import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BiSolidCommentDetail, BiSolidLike, BiTrash } from "react-icons/bi";
import { TbBriefcase, TbSchool, TbClock } from "react-icons/tb";
import FormatDate from '../CommentCard/FormatDate';
import { useSelector, useDispatch } from 'react-redux';
import PopupComp from '../Popups/PopupComp';
import { deletePost } from '../../Actions/jobs';

const PALETTES = [
  { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
  { bg: '#F5F3FF', text: '#6D28D9', border: '#DDD6FE' },
  { bg: '#ECFDF5', text: '#065F46', border: '#A7F3D0' },
  { bg: '#FFF7ED', text: '#9A3412', border: '#FED7AA' },
  { bg: '#FFF1F2', text: '#9F1239', border: '#FECDD3' },
];

function CompanyAvatar({ name }) {
  const initials = name ? name.trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') : '?';
  const safeNameCode = name?.charCodeAt(0) ?? 0;
  const index = PALETTES.length > 0 ? safeNameCode % PALETTES.length : 0;
  const p = PALETTES[index] || PALETTES[0]; 

  return (
    <div
      style={{ background: p.bg, color: p.text, border: `1px solid ${p.border}` }}
      className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs flex-shrink-0 tracking-tight shadow-sm"
    >
      {initials}
    </div>
  );
}

function OutcomeChip({ status }) {
  const ok = status === 'Selected';
  return (
    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${
      ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-600'
    }`}>
      <span className={`w-1 h-1 rounded-full ${ok ? 'bg-emerald-500' : 'bg-rose-500'}`} />
      {status}
    </div>
  );
}

function ProfilePostCard({ post, showAuthor }) {
  const { user } = useSelector(s => s.user);
  const dispatch = useDispatch();
  const isOwner = user && post?.owner?._id === user?._id;
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const handleDelete = () => {
    dispatch(deletePost(post._id));
    setShowDeletePopup(false);
  };

  return (
    <>
      <div className="group relative bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-200 overflow-hidden">
        <div className="p-4 sm:p-5">
          {/* Header */}
          <div className="flex justify-between items-start mb-3 gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <CompanyAvatar name={post.company} />
              <div className="min-w-0">
                <h3 className="font-bold text-slate-800 text-sm leading-tight truncate">
                  {post.company}
                </h3>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className="flex items-center gap-1 text-[9px] font-bold text-slate-500 uppercase">
                    <TbBriefcase size={10} /> {post.type}
                  </span>
                </div>
              </div>
            </div>
            <OutcomeChip status={post.status} />
          </div>

          {/* Title */}
          <Link to={`/post/${post._id}`} className="block mb-3">
            <h2 className="text-sm font-bold text-slate-900 leading-snug group-hover:text-blue-600 transition-colors line-clamp-2">
              {post.caption}
            </h2>
          </Link>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-50">
            {showAuthor ? (
              <div className="flex items-center gap-2 min-w-0">
                {post?.owner?.profileImage ? (
                  <img src={post.owner.profileImage} alt="User" className="w-5 h-5 rounded-full object-cover" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-500">
                    {post?.owner?.name?.charAt(0) || '?'}
                  </div>
                )}
                <span className="text-[10px] font-bold text-slate-600 truncate max-w-[80px]">
                  {post?.owner?.name}
                </span>
                <span className="w-1 h-1 rounded-full bg-slate-200" />
                <span className="flex items-center gap-1 text-[9px] font-semibold text-slate-400">
                  <TbClock size={10} /> {FormatDate(post.createdAt)}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-[10px] font-semibold text-slate-400">
                <TbClock size={11} /> {FormatDate(post.createdAt)}
              </div>
            )}

            <div className="flex items-center gap-1">
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-slate-400 bg-slate-50">
                <BiSolidLike size={12} /> {post?.likesCount ?? 0}
              </div>
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-slate-400 bg-slate-50">
                <BiSolidCommentDetail size={12} /> {post?.commentsCount ?? 0}
              </div>

              {isOwner && (
                <button onClick={() => setShowDeletePopup(true)} className="ml-1 p-1 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all">
                  <BiTrash size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showDeletePopup && (
        <PopupComp onClose={() => setShowDeletePopup(false)}>
           <div className="bg-white p-6 rounded-3xl text-center max-w-sm mx-auto shadow-2xl">
            <div className="w-12 h-12 bg-rose-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-rose-100">
              <BiTrash size={22} className="text-rose-500" />
            </div>
            <h3 className="text-lg font-black text-slate-900 mb-1.5">Delete this post?</h3>
            <p className="text-slate-500 text-xs sm:text-sm mb-5">This action cannot be undone.</p>
            <div className="flex flex-col gap-2">
              <button onClick={handleDelete} className="w-full py-3 rounded-xl font-bold text-white bg-rose-600 hover:bg-rose-700 transition-all">
                Yes, Delete
              </button>
              <button onClick={() => setShowDeletePopup(false)} className="w-full py-3 rounded-xl font-semibold text-slate-500 hover:bg-slate-50 transition-all">
                Cancel
              </button>
            </div>
          </div>
        </PopupComp>
      )}
    </>
  );
}

export default ProfilePostCard;
